import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddOnOptionSelection, Product } from '@la/types';
import { RegistrationAddOnsState } from 'domains/MemberRegistration/MemberRegistration.types';

const initialState: RegistrationAddOnsState = {
  addOnOptionSelections: [],
  addOnProducts: [],
  registrationCost: undefined,
  registrationId: undefined,
  registrationType: undefined,
  selectionsWithErrors: [],
  taxRate: 0,
};

const registrationAddOnsSlice = createSlice({
  name: 'registrationAddOns',
  initialState,
  reducers: {
    resetRegistrationAddOns: () => initialState,
    setRegistrationAddOns(state, action: PayloadAction<Product[]>) {
      state.addOnProducts = action.payload;
    },
    setRegistrationCost(state, action: PayloadAction<number>) {
      state.registrationCost = action.payload;
    },
    setRegistrationType(state, action: PayloadAction<'player' | 'staff'>) {
      state.registrationType = action.payload;
    },
    setSelectionsWithErrors(state, action: PayloadAction<string[]>) {
      state.selectionsWithErrors = action.payload;
    },
    setTaxRate(state, action: PayloadAction<number>) {
      state.taxRate = action.payload;
    },
    setAddOnOptionSelections(
      state,
      action: PayloadAction<AddOnOptionSelection[]>
    ) {
      state.addOnOptionSelections = action.payload;
    },
    // This should only be used by required add-ons.
    updateRequiredAddOnSelection(
      state,
      action: PayloadAction<{
        addOnId: number;
        propertyId: string;
        value: string;
      }>
    ) {
      const { addOnId, propertyId, value } = action.payload;

      return {
        ...state,
        addOnOptionSelections: state.addOnOptionSelections.map(
          (addOnOptionSelection: AddOnOptionSelection) => {
            // Only update the matching addOnId
            if (addOnOptionSelection.addOnId !== addOnId) {
              return addOnOptionSelection;
            }

            // Find existing selection with matching optionId
            const existingSelectionIndex =
              addOnOptionSelection.selections.findIndex(
                (selection) => selection.optionId === propertyId
              );

            // If value is missing, remove existing selection or don't add new one
            if (!value) {
              if (existingSelectionIndex !== -1) {
                // Remove existing selection
                return {
                  ...addOnOptionSelection,
                  selections: addOnOptionSelection.selections.filter(
                    (selection, index) => index !== existingSelectionIndex
                  ),
                };
              }
              // No existing selection and missing value, return as is
              return addOnOptionSelection;
            }

            // If selection exists, update its selectedValue
            if (existingSelectionIndex !== -1) {
              return {
                ...addOnOptionSelection,
                selections: addOnOptionSelection.selections.map(
                  (selection, index) =>
                    index === existingSelectionIndex
                      ? { ...selection, selectedValue: value }
                      : selection
                ),
              };
            }

            // If selection doesn't exist and value is valid, add new one
            return {
              ...addOnOptionSelection,
              selections: [
                ...addOnOptionSelection.selections,
                { optionId: propertyId, selectedValue: value },
              ],
            };
          }
        ),
      };
    },
    updateOptionalAddOns(
      state,
      action: PayloadAction<{
        updatedAddOnOptionSelections: AddOnOptionSelection[];
      }>
    ) {
      const { updatedAddOnOptionSelections } = action.payload;

      return {
        ...state,
        addOnOptionSelections: updatedAddOnOptionSelections,
      };
    },
  },
});

export const {
  resetRegistrationAddOns,
  setAddOnOptionSelections,
  setRegistrationAddOns,
  setRegistrationType,
  setSelectionsWithErrors,
  setTaxRate,
  updateRequiredAddOnSelection,
  updateOptionalAddOns,
} = registrationAddOnsSlice.actions;

export default registrationAddOnsSlice.reducer;
